/**
 * useWebSocket — Real-time event hook for the SocialScale dashboard (T-061).
 *
 * Connects to /ws, auto-reconnects on disconnect, and dispatches events by
 * channel so components can subscribe to specific event types.
 */

import { useEffect, useRef, useCallback } from 'react'

export interface WsEvent {
  channel: string
  event_type: string
  payload: Record<string, unknown>
}

type EventHandler = (event: WsEvent) => void

function resolveWebSocketUrl(): string | null {
  if (import.meta.env.VITE_WS_ENABLED === 'false') return null
  if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL

  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${scheme}://${window.location.host}/ws`
}

const WS_URL = resolveWebSocketUrl()
const RECONNECT_DELAY_MS = 3000

export function useWebSocket(
  handler: EventHandler,
  channels?: string[],
) {
  const wsRef = useRef<WebSocket | null>(null)
  const handlerRef = useRef(handler)
  const channelsRef = useRef(channels)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unmounted = useRef(false)

  // Keep handler ref fresh without re-connecting
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    channelsRef.current = channels
  }, [channels])

  const connect = useCallback(() => {
    if (unmounted.current || !WS_URL) return

    let ws: WebSocket
    try {
      ws = new WebSocket(WS_URL)
    } catch {
      // Never crash the React tree on constructor failures (e.g. mixed content).
      if (!unmounted.current) {
        reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS)
      }
      return
    }
    wsRef.current = ws

    ws.onopen = () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current)
        reconnectTimer.current = null
      }
    }

    ws.onmessage = (e: MessageEvent) => {
      try {
        const event: WsEvent = JSON.parse(e.data as string)
        const channels = channelsRef.current
        if (!channels || channels.includes(event.channel)) {
          handlerRef.current(event)
        }
      } catch {
        // Ignore malformed frames
      }
    }

    ws.onclose = () => {
      wsRef.current = null
      if (!unmounted.current) {
        reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS)
      }
    }

    ws.onerror = () => {
      ws.close()
    }
  }, [])

  useEffect(() => {
    unmounted.current = false
    connect()
    return () => {
      unmounted.current = true
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [connect])
}
